import { useEffect, useMemo, useState } from "react";
import api, { fmtBRL, fmtInt, fmtNum } from "../lib/api";
import PageHeader from "../components/PageHeader";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { RefreshCw, AlertTriangle, Calendar, Clock, CheckCircle2, Search } from "lucide-react";
import { toast } from "sonner";

function iso(d) { return d.toISOString().slice(0, 10); }
function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

const PERIOD_PRESETS = [
  { id: "this_month", label: "Este mês", compute: () => ({ s: iso(startOfMonth(new Date())), e: iso(endOfMonth(new Date())) }) },
  { id: "next_30", label: "Próximos 30 dias", compute: () => { const t = new Date(); return { s: iso(t), e: iso(addDays(t, 30)) }; } },
  { id: "next_90", label: "Próximos 90 dias", compute: () => { const t = new Date(); return { s: iso(t), e: iso(addDays(t, 90)) }; } },
  { id: "last_30", label: "Últimos 30 dias", compute: () => { const t = new Date(); return { s: iso(addDays(t, -30)), e: iso(t) }; } },
  { id: "ytd", label: "Ano atual", compute: () => { const t = new Date(); return { s: `${t.getFullYear()}-01-01`, e: `${t.getFullYear()}-12-31` }; } },
  { id: "all", label: "Tudo", compute: () => ({ s: "2025-01-01", e: "2027-12-31" }) },
  { id: "custom", label: "Personalizado", compute: null },
];

const GROUPS = [
  { id: "day", label: "Dia a dia" },
  { id: "week", label: "Semana a semana" },
  { id: "month", label: "Mês a mês" },
];

const STATUS_COLORS = {
  PAGO: "var(--ifl-success)",
  VENCIDO: "var(--ifl-danger)",
  A_VENCER: "var(--ifl-mustard)",
};

const initialPeriod = PERIOD_PRESETS.find(p => p.id === "this_month").compute();

function FilterPill({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="px-3 py-1.5 rounded-full border font-display uppercase tracking-wider text-xs transition-all"
      style={{
        background: active ? "var(--ifl-text)" : "transparent",
        color: active ? "#fff" : "var(--ifl-text)",
        borderColor: active ? "var(--ifl-text)" : "var(--ifl-border)",
      }}
    >
      {children}
    </button>
  );
}

function KPI({ label, value, hint, accent, testId, icon: Icon }) {
  return (
    <div className="ifl-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div className="label-caps">{label}</div>
        {Icon && <Icon size={14} strokeWidth={1.5} style={{ color: accent || "var(--ifl-muted)" }} />}
      </div>
      <div className="font-display text-3xl tabular-nums" style={{ color: accent || "var(--ifl-text)", letterSpacing: "-0.02em" }}>
        {value}
      </div>
      {hint && <div className="font-body text-xs mt-2" style={{ color: "var(--ifl-muted)" }}>{hint}</div>}
    </div>
  );
}

function statusBadge(s) {
  const colors = { PAGO: "var(--ifl-success)", VENCIDO: "var(--ifl-danger)", A_VENCER: "var(--ifl-mustard)" };
  return (
    <span className="font-display text-xs uppercase tracking-wider px-2 py-0.5 rounded-full border" style={{ color: colors[s] || "var(--ifl-muted)", borderColor: colors[s] || "var(--ifl-border)" }}>
      {s?.replace("_", " ")}
    </span>
  );
}

export default function ContasAPagar() {
  const [period, setPeriod] = useState(initialPeriod);
  const [presetId, setPresetId] = useState("this_month");
  const [groupBy, setGroupBy] = useState("day");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const applyPreset = (id) => {
    setPresetId(id);
    const p = PERIOD_PRESETS.find(x => x.id === id);
    if (p && p.compute) setPeriod(p.compute());
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, list, k] = await Promise.all([
        api.get(`/contas-pagar/summary?start_date=${period.s}&end_date=${period.e}&group_by=${groupBy}${statusFilter ? `&status=${statusFilter}` : ""}`),
        api.get(`/contas-pagar?start_date=${period.s}&end_date=${period.e}${statusFilter ? `&status=${statusFilter}` : ""}&limit=1000`),
        api.get(`/contas-pagar/kpis`),
      ]);
      setSummary(s.data);
      setItems(list.data);
      setKpis(k.data);
    } catch (e) {
      toast.error("Falha ao carregar contas a pagar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period.s, period.e, groupBy, statusFilter]);

  const refresh = async () => {
    setSyncing(true);
    const t = toast.loading("Sincronizando contas a pagar do Omie…");
    try {
      const r = await api.post("/sync/omie/contas-pagar", {});
      toast.success(`Sincronizado: ${r.data.fetched} contas (${r.data.inserted} novas, ${r.data.updated} atualizadas)`, { id: t });
      load();
    } catch (e) {
      toast.error("Falha ao sincronizar", { id: t });
    } finally {
      setSyncing(false);
    }
  };

  const exportCSV = () => {
    if (!items?.length) return;
    const header = ["Vencimento", "Status", "Fornecedor", "Documento", "Tipo", "Valor", "Categoria"];
    const lines = [header.join(",")];
    items.forEach(i => {
      lines.push([
        i.data_vencimento,
        i.status,
        `"${(i.fornecedor_nome || `#${i.fornecedor_id}` || "—").replace(/"/g, "'")}"`,
        i.numero_documento || "",
        i.tipo_documento || "",
        i.valor,
        i.categoria || "",
      ].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `contas_a_pagar_${period.s}_${period.e}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const filteredItems = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(i =>
      (i.fornecedor_nome || "").toLowerCase().includes(q) ||
      (i.numero_documento || "").toLowerCase().includes(q) ||
      String(i.fornecedor_id || "").includes(q)
    );
  }, [items, search]);

  return (
    <div data-testid="contas-pagar-page">
      <PageHeader
        eyebrow="Financeiro · Pagar"
        title="CONTAS A"
        accent="pagar."
        description="Vencimentos do Omie consolidados por dia, semana ou mês. Atualizado sob demanda."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={exportCSV} className="ifl-btn-outline" data-testid="export-cp-csv" disabled={!items?.length}>Exportar CSV</button>
            <button onClick={refresh} className="ifl-btn" disabled={syncing} data-testid="refresh-cp">
              <RefreshCw size={14} strokeWidth={2} className={syncing ? "ifl-pulse" : ""} />
              {syncing ? "Sincronizando…" : "Sincronizar Omie"}
            </button>
          </div>
        }
        testId="cp-header"
      />

      {/* KPIs */}
      {kpis && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <KPI testId="kpi-vencido" label="Vencido" value={fmtBRL(kpis.vencido?.valor || 0)} hint={`${kpis.vencido?.count || 0} título(s)`} accent="var(--ifl-danger)" icon={AlertTriangle} />
          <KPI testId="kpi-7d" label="A vencer · 7 dias" value={fmtBRL(kpis.a_vencer_7d?.valor || 0)} hint={`${kpis.a_vencer_7d?.count || 0} título(s)`} accent="var(--ifl-mustard)" icon={Clock} />
          <KPI testId="kpi-30d" label="A vencer · 30 dias" value={fmtBRL(kpis.a_vencer_30d?.valor || 0)} hint={`${kpis.a_vencer_30d?.count || 0} título(s)`} accent="var(--ifl-text)" icon={Calendar} />
          <KPI testId="kpi-aberto" label="Total em aberto" value={fmtBRL(kpis.total_em_aberto?.valor || 0)} hint={`${kpis.total_em_aberto?.count || 0} título(s)`} accent="var(--ifl-terracotta)" />
          <KPI testId="kpi-pago-ano" label="Pago · últ. 12m" value={fmtBRL(kpis.pago_ultimos_365d?.valor || 0)} hint={`${kpis.pago_ultimos_365d?.count || 0} título(s)`} accent="var(--ifl-success)" icon={CheckCircle2} />
        </div>
      )}

      {/* Filters */}
      <div className="ifl-card p-5 mb-6" data-testid="cp-filters">
        <div className="flex flex-wrap gap-2 items-center mb-4">
          <div className="label-caps mr-2">Período</div>
          {PERIOD_PRESETS.map(p => (
            <FilterPill key={p.id} active={presetId === p.id} onClick={() => applyPreset(p.id)} testId={`cp-preset-${p.id}`}>{p.label}</FilterPill>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-end">
          <div>
            <div className="label-caps mb-1">De</div>
            <input type="date" value={period.s} onChange={(e) => { setPeriod({ ...period, s: e.target.value }); setPresetId("custom"); }} className="ifl-input w-full" data-testid="cp-start" />
          </div>
          <div>
            <div className="label-caps mb-1">Até</div>
            <input type="date" value={period.e} onChange={(e) => { setPeriod({ ...period, e: e.target.value }); setPresetId("custom"); }} className="ifl-input w-full" data-testid="cp-end" />
          </div>
          <div>
            <div className="label-caps mb-1">Agrupar por</div>
            <div className="flex gap-1">
              {GROUPS.map(g => (
                <FilterPill key={g.id} active={groupBy === g.id} onClick={() => setGroupBy(g.id)} testId={`cp-group-${g.id}`}>{g.label}</FilterPill>
              ))}
            </div>
          </div>
          <div>
            <div className="label-caps mb-1">Status</div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="ifl-input w-full" data-testid="cp-status-filter">
              <option value="">Todos</option>
              <option value="A_VENCER">A vencer</option>
              <option value="VENCIDO">Vencido</option>
              <option value="PAGO">Pago</option>
            </select>
          </div>
          <div>
            <div className="label-caps mb-1">Buscar</div>
            <div className="relative">
              <Search size={14} strokeWidth={1.5} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ifl-muted)" }} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Fornecedor ou nº doc" className="ifl-input w-full" style={{ paddingLeft: 34 }} data-testid="cp-search" />
            </div>
          </div>
        </div>
      </div>

      {/* Consolidated bar chart */}
      {summary && summary.series?.length > 0 && (
        <div className="ifl-card p-6 mb-6" data-testid="cp-chart-card">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="label-caps">Vencimentos consolidados</div>
              <div className="font-italic-serif text-xl mt-1">{GROUPS.find(g => g.id === groupBy)?.label.toLowerCase()}.</div>
            </div>
            <div className="font-body text-xs flex items-center gap-4" style={{ color: "var(--ifl-muted)" }}>
              <span><span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: "var(--ifl-mustard)" }}></span>A vencer</span>
              <span><span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: "var(--ifl-danger)" }}></span>Vencido</span>
              <span><span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: "var(--ifl-success)" }}></span>Pago</span>
            </div>
          </div>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={summary.series} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#E5E1D8" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="period" tick={{ fill: "#6B6E76", fontSize: 11, fontFamily: "DM Mono" }} tickLine={false} axisLine={{ stroke: "#E5E1D8" }} />
                <YAxis tick={{ fill: "#6B6E76", fontSize: 10, fontFamily: "DM Mono" }} tickLine={false} axisLine={{ stroke: "#E5E1D8" }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5E1D8", borderRadius: 8, fontFamily: "DM Sans, sans-serif" }} formatter={(v, name) => [fmtBRL(v), name === "pago" ? "Pago" : name === "vencido" ? "Vencido" : "A vencer"]} />
                <Bar dataKey="a_vencer" stackId="a" fill="#C48A2E" name="a_vencer" />
                <Bar dataKey="vencido" stackId="a" fill="#C44C2E" name="vencido" />
                <Bar dataKey="pago" stackId="a" fill="#4F7C53" name="pago" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
            <div>
              <div className="label-caps">Total no período</div>
              <div className="font-display text-2xl tabular-nums mt-1">{fmtBRL(summary.totals.valor_total)}</div>
              <div className="font-body text-xs" style={{ color: "var(--ifl-muted)" }}>{summary.totals.count} título(s)</div>
            </div>
            <div>
              <div className="label-caps" style={{ color: "var(--ifl-success)" }}>Pago</div>
              <div className="font-display text-2xl tabular-nums mt-1" style={{ color: "var(--ifl-success)" }}>{fmtBRL(summary.totals.by_status.PAGO || 0)}</div>
            </div>
            <div>
              <div className="label-caps" style={{ color: "var(--ifl-mustard)" }}>A vencer</div>
              <div className="font-display text-2xl tabular-nums mt-1" style={{ color: "var(--ifl-mustard)" }}>{fmtBRL(summary.totals.by_status.A_VENCER || 0)}</div>
            </div>
            <div>
              <div className="label-caps" style={{ color: "var(--ifl-danger)" }}>Vencido</div>
              <div className="font-display text-2xl tabular-nums mt-1" style={{ color: "var(--ifl-danger)" }}>{fmtBRL(summary.totals.by_status.VENCIDO || 0)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Top fornecedores */}
      {summary && summary.top_fornecedores?.length > 0 && (
        <div className="ifl-card p-6 mb-6" data-testid="cp-top-fornec-card">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="label-caps">Top fornecedores</div>
              <div className="font-italic-serif text-xl mt-1">por valor no período.</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {summary.top_fornecedores.slice(0, 10).map((f, i) => {
              const max = summary.top_fornecedores[0].valor;
              const w = max ? (f.valor / max) * 100 : 0;
              const name = f.fornecedor_nome || `Fornecedor #${f.fornecedor_id || "?"}`;
              return (
                <div key={`${f.fornecedor_id}-${i}`} className="flex flex-col gap-1.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span className="font-display text-xs" style={{ color: "var(--ifl-muted)" }}>#{i + 1}</span>
                      <span className="font-body text-sm truncate" style={{ color: "var(--ifl-text)" }}>{name}</span>
                    </div>
                    <div className="font-display tabular-nums text-sm shrink-0">{fmtBRL(f.valor)}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-1.5 rounded-full" style={{ background: "var(--ifl-border)" }}>
                      <div style={{ width: `${w}%`, height: "100%", background: "var(--ifl-text)", borderRadius: 999 }} />
                    </div>
                    <div className="font-display text-xs tabular-nums shrink-0" style={{ color: "var(--ifl-muted)" }}>{f.count} título(s)</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Item by item table */}
      <div className="ifl-card overflow-hidden mb-6" data-testid="cp-items-card">
        <div className="px-6 py-4 flex items-center justify-between flex-wrap gap-2 border-b" style={{ borderColor: "var(--ifl-border)" }}>
          <div>
            <div className="label-caps">Lançamentos do período</div>
            <div className="font-italic-serif text-lg mt-1">item por item.</div>
          </div>
          <div className="font-display text-xs uppercase tracking-widest" style={{ color: "var(--ifl-muted)" }}>{filteredItems.length} de {items.length} título(s)</div>
        </div>
        <div className="overflow-x-auto">
          <table className="ifl-table" data-testid="cp-items-table">
            <thead>
              <tr>
                <th>Vencimento</th>
                <th>Status</th>
                <th>Fornecedor</th>
                <th>Documento</th>
                <th>Tipo</th>
                <th className="text-right">Valor</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 && !loading && (
                <tr><td colSpan={6} className="text-center py-12" style={{ color: "var(--ifl-muted)" }}>Nenhum lançamento no período.</td></tr>
              )}
              {loading && (
                <tr><td colSpan={6} className="text-center py-12" style={{ color: "var(--ifl-muted)" }}>Carregando…</td></tr>
              )}
              {filteredItems.map((i) => (
                <tr key={i.id_unico}>
                  <td className="font-mono text-sm">{i.data_vencimento?.slice(5).replace("-", "/")}<span className="ml-2 font-display text-xs" style={{ color: "var(--ifl-muted)" }}>{i.data_vencimento?.slice(0, 4)}</span></td>
                  <td>{statusBadge(i.status)}</td>
                  <td className="text-sm">{i.fornecedor_nome || <span style={{ color: "var(--ifl-muted)" }}>#{i.fornecedor_id || "—"}</span>}</td>
                  <td className="font-mono text-xs">{i.numero_documento || i.numero_documento_fiscal || "—"}</td>
                  <td className="font-display text-xs uppercase tracking-wider" style={{ color: "var(--ifl-muted)" }}>{i.tipo_documento || "—"}</td>
                  <td className="text-right font-display tabular-nums">{fmtBRL(i.valor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
